// Centralized API endpoint paths
// Grouped by feature area and exported for reuse across the app

// Auth
export const AUTH_ENDPOINTS = {
  // ── blaze (legacy) ──────────────────────────────────────────────────────────
  // ⚠️ LOGIN_EMAIL_PHONE / LOGIN_CONFIRM are NO LONGER the login path. They remain
  // only for the CONTACT-CHANGE resend in OtpPopup, which is a blaze flow and is
  // migration-order-sensitive (D21 tags the four contact-change endpoints
  // auth-critical). Do not reuse them for sign-in.
  LOGIN_EMAIL_PHONE: "/api/loginEmailPhone",
  LOGIN_CONFIRM: "/api/login/confirm",
  SOCIAL_LOGIN: "/auth/social",

  // ── kernel (relative to KERNEL_URL, which already carries /api/v3 — D21) ─────
  KERNEL_LOGIN_START: "/auth/login/start",
  KERNEL_LOGIN_VERIFY: "/auth/login/verify",
  KERNEL_LOGOUT: "/auth/logout",
  KERNEL_ME: "/me",
  KERNEL_SOCIAL_CALLBACK: "/auth/social/callback",
  KERNEL_AUTH_METHODS: "/me/auth-methods",
  KERNEL_SOFT_DELETE: "/me",
  REFRESH_TOKEN: "/auth/refresh",
} as const;

// Patient-initiated org sharing (ACDPA-186) — kernel routes, relative to KERNEL_URL.
export const ORG_SHARING_ENDPOINTS = {
  LIST: "/me/organizations",
  SHARE: (orgId: number | string) => `/me/organizations/${orgId}/share`,
  UNSHARE: (orgId: number | string) => `/me/organizations/${orgId}/share`,
  // ACDPA-315 / D77. ⚠️ Not deployed to dev as of 2026-09-08 (all three return 404 there).
  INVITATION_ACCEPT: (orgId: number | string) =>
    `/me/organizations/${orgId}/invitation/accept`,
  INVITATION_DECLINE: (orgId: number | string) =>
    `/me/organizations/${orgId}/invitation/decline`,
  // Leave, for an association of ANY origin — the only route that reaches an org-created link.
  LEAVE_ASSOCIATION: (orgId: number | string) => `/me/organizations/${orgId}/association`,
} as const;

// User
export const USER_ENDPOINTS = {
  CREATE: "/user/create",
  UPDATE_PROFILE: "/user/updateProfile",
  SETTINGS: "/user/settings",
  AVATAR_UPLOAD: "/user/avatar",
  DELETE_ACCOUNT: "/user/account",
  CHANGE_PASSWORD: "/user/password",
  PASSWORD_RESET_REQUEST: "/user/password/reset-request",
  ACTIVITY: "/user/activity",
  UPDATE_PROFILE_PICTURE: "/user/updateProfilePicture",
} as const;

// Profiles and preferences
export const PROFILE_ENDPOINTS = {
  GET_BY_ID: "/api/getProfileById",
  DIAGNOSIS_LIST: "/api/diagnosis",
  USER_PREFERENCES: "/api/user-preferences",
} as const;

export const NOTIFICATION_ENDPOINTS = {
  SETTINGS: "/api/notification-settings",
  SETTINGS_PENDING: "/api/notification-settings-pending",
} as const;

export const CONTACT_CHANGE_ENDPOINTS = {
  CHANGE_PHONE: "/api/change-phone-number",
  VERIFY_PHONE_OTP: "/api/Verify-change-PhoneOtp",
  CHANGE_EMAIL: "/api/change-email",
  VERIFY_EMAIL_OTP: "/api/Verify-change-EmailOtp",
} as const;

export const ACCOUNT_ENDPOINTS = {
  USER_EXISTS: "/api/login/user-exists",
  // ⚠️ NO LONGER CALLED — replaced by KERNEL_SOFT_DELETE. Calling both 409s.
  SOFT_DELETE: "/api/delete-account",
} as const;

// Devices
export const DEVICE_ENDPOINTS = {
  LIST: "/devices",
  ATTACH: "/devices",
  DETACH: "/devices/",
} as const;

// Goals
export const GOALS_ENDPOINTS = {
  LIST: "/api/user_goals",
  BY_ID: (goalId: number | string) => `/api/user_goals/${goalId}`,
} as const;

// Activity/Dashboard
export const ACTIVITY_ENDPOINTS = {
  RECORD_ADHERENCE: "/api/activity-goals/adherence",
  DAILY_STATUS: (profileId: number | string) => `/api/activity-goals/adherence/${profileId}`,
  DAILY_THERAPIES: (profileId: number | string) => `/api/activity-goals/daily/${profileId}`,
  WEEKLY_ADHERENCE: (profileId: number | string) => `/api/activity-goals/weekly-adherence/${profileId}`,
  DAILY_ADHERENCE: (profileId: number | string) => `/api/activity-goals/adherence/daily/${profileId}`,
  RANGE_ADHERENCE: (profileId: number | string) => `/api/activity-goals/adherence/range/${profileId}`,
  PRESSURE_SUMMARY: "/api/pressure-summary",
} as const;

// Graphs
export const GRAPH_ENDPOINTS = {
  PRESSURE_FLOW: "/api/pressure-flow-graph",
} as const;

// Social / Reflections / Posts
export const POSTS_ENDPOINTS = {
  FEED: "/api/reflections/feed",
  POST: "/api/posts/",
  BY_ID: (postId: string | number) => `/api/posts/${postId}`,
  REACT: (postId: string | number) => `/api/posts/${postId}/react`,
  COMMENTS: (postId: string | number) => `/api/posts/${postId}/comments`,
  REACTIONS: (postId: string | number) => `/api/posts/${postId}/reactions`,
  COMMENT_REACT: (commentId: string | number) => `/api/comments/${commentId}/react`,
  COMMENT_BY_ID: (commentId: string | number) => `/api/comments/${commentId}`,
  COMMENT_REPLIES: (commentId: string | number) => `/api/comments/${commentId}/replies`,
  UPLOAD_REQUEST: "/api/posts/upload/request",
  UPLOAD_VERIFY: (uploadId: string) => `/api/posts/upload/verify/${uploadId}`,
  UPLOAD_BACKEND: "/api/posts/upload/backend",
} as const;

// Invitations / Following / Data sharing
export const INVITATION_ENDPOINTS = {
  SEND_INVITE: "/api/v2/invitations/sendInvite",
  INVITATION_DETAILS: "/api/invitationdetails",
  SENT_INVITATIONS: "/api/v2/invitations/sent",
} as const;

export const SOCIAL_RELATIONSHIP_ENDPOINTS = {
  FOLLOW: "/api/follow",
  UNFOLLOW: "/api/unfollow",
  ACCEPT_FOLLOW_REQUEST: "/api/accept-follow-request",
  REJECT_FOLLOW_REQUEST: "/api/reject-follow-request",
  ACCEPT_INVITATION: "/api/accept-invitation",
} as const;

// Therapy
export const THERAPY_ENDPOINTS = {
  PREV_ADS_SCORE: "/fetchprevadsscore",
  ADHERENCE_SCORE_MAP: "/adheranceScoreMap",
} as const;

// Documents
export const DOCUMENTS_ENDPOINTS = {
  BY_TYPE: "/api/documents",
} as const;

// Reports (PDF generation)
export const REPORT_ENDPOINTS = {
  MIE_PDF: "/api/user/getMIEPdfReport",
  PEP_PDF: "/api/user/getPEPPdfReport",
} as const;

// Misc / Device logs
export const MISC_ENDPOINTS = {
  DEVICE_LOG_RECEIVER: "/tesla.data.server/rest/receiver",
} as const;


// ── Patient engagement (KERNEL, not blaze) — Epic 12 patient side, E12c ───────────────────
//
// ⛔ THESE ARE KERNEL PATHS AND MUST ONLY EVER BE PASSED TO `kernelClient`. `kernelClient`'s
// base URL already ends in `/api/v3`, so the paths here start at `/me`. Handing one of them to
// `apiClient` would send a patient's message to blaze, which has no message model — it would
// 404 and the question would be lost.
//
// ⛔ NO PATH TAKES A `profile_id` OR AN `org_id`. Every one is self-scoped; the kernel resolves
// both from the session. A helper that accepted either would be the wrong shape.
export const ENGAGEMENT_ENDPOINTS = {
  /** ACDPA-228 / URS_12.6 — the flags the UI mirrors. Never cache the response. */
  FEATURES: "/me/engagement/features",
  /**
   * E12d/R2 — the one call the app makes at sign-in and on app open. It ISSUES any milestone
   * survey that has fallen due (there is no worker on the kernel, by ruling) and returns the
   * three counts the badges read. A POST because it writes.
   */
  SYNC: "/me/engagement/sync",
  THREADS: "/me/engagement/threads",
  THREAD: (threadId: number | string) => `/me/engagement/threads/${threadId}`,
  THREAD_MESSAGES: (threadId: number | string) =>
    `/me/engagement/threads/${threadId}/messages`,
  THREAD_READ: (threadId: number | string) => `/me/engagement/threads/${threadId}/read`,
  MOOD_CHECKINS: "/me/engagement/mood-checkins",
  /** F-483 — PATCH: change today's own check-in (same local day only, server-enforced). */
  MOOD_CHECKIN: (checkinId: number | string) => `/me/engagement/mood-checkins/${checkinId}`,
  SURVEYS: "/me/engagement/surveys",
  SURVEY_RESPONSE: (surveyId: number | string) =>
    `/me/engagement/surveys/${surveyId}/response`,
  GOALS: "/me/engagement/goals",
} as const;

export type EndpointPath = string | ((...args: any[]) => string);
const USER_PREFERENCES_ACTIONS = "/api/user-preferences/actions";
export const ENDPOINTS = {
  AUTH: AUTH_ENDPOINTS,
  USER: USER_ENDPOINTS,
  PROFILE: PROFILE_ENDPOINTS,
  NOTIFICATIONS: NOTIFICATION_ENDPOINTS,
  CONTACT: CONTACT_CHANGE_ENDPOINTS,
  ACCOUNT: ACCOUNT_ENDPOINTS,
  DEVICES: DEVICE_ENDPOINTS,
  GOALS: GOALS_ENDPOINTS,
  ACTIVITY: ACTIVITY_ENDPOINTS,
  GRAPHS: GRAPH_ENDPOINTS,
  POSTS: POSTS_ENDPOINTS,
  INVITATIONS: INVITATION_ENDPOINTS,
  SOCIAL: SOCIAL_RELATIONSHIP_ENDPOINTS,
  THERAPY: THERAPY_ENDPOINTS,
  DOCUMENTS: DOCUMENTS_ENDPOINTS,
  MISC: MISC_ENDPOINTS,
  REPORTS: REPORT_ENDPOINTS,
  USER_ACTIONS: USER_PREFERENCES_ACTIONS,
  ENGAGEMENT: ENGAGEMENT_ENDPOINTS,
} as const;
